// Prices waiting for the office's OK (estimates.awaitingPriceOk, Q44): option lines a technician
// priced below their limit, below cost or under the custom-line floor. The words for why a line
// waits (sent only to roles that may see our cost) and how far under the price list it is.

export interface WaitingLine {
  lineId: string;
  description: string;
  quantity: string;
  unitPriceCents: number;
  bookPriceCents: number | null;
  why?: 'below_limit' | 'below_cost' | 'below_floor';
  /** How far under the price list, in total (positive: under); null for a custom line. */
  differenceCents: number | null;
}
export interface WaitingEstimate {
  estimateId: string;
  number: string;
  title: string;
  jobId: string;
  jobNumber: string;
  customerName: string;
  techName: string | null;
  options: {
    optionId: string;
    title: string;
    lines: WaitingLine[];
    differenceCents: number;
    /** BL-059: the option waits on its margin; the numbers only for roles that see our cost. */
    margin?: { pct?: number | null; minPct?: number };
    /** BL-059: the customer signed it while it waited; the OK completes that sale. */
    signed?: { signerName: string; signedAt: string };
  }[];
}

/** Who OKs prices: whoever holds estimates.approve (the owner, the manager and the dispatcher). */
export const PRICE_OK_PERMS = ['estimates.approve'];

export const WHY_WORDS: Record<NonNullable<WaitingLine['why']>, string> = {
  below_limit: 'below the technician’s own limit',
  below_cost: 'below our cost',
  below_floor: 'under the custom-line floor',
};

const dollars = (cents: number) =>
  new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(cents / 100);

/** "Water heater flush: $95.00 each, $40.00 under the price list (below our cost)". */
export function waitingLineText(l: WaitingLine): string {
  const q = Number(l.quantity);
  const each = `${q === 1 ? '' : `${q} × `}${dollars(l.unitPriceCents)}${q === 1 ? '' : ' each'}`;
  const under =
    l.differenceCents === null
      ? 'a custom line'
      : l.differenceCents > 0
        ? `${dollars(l.differenceCents)} under the price list`
        : 'at the price list';
  return `${l.description}: ${each}, ${under}${l.why ? ` (${WHY_WORDS[l.why]})` : ''}`;
}

/** "Its margin is 37.5 %, under the 40 % minimum" (BL-059), or without the numbers for those who don't see cost. */
export function waitingMarginText(m: NonNullable<WaitingEstimate['options'][number]['margin']>): string {
  return typeof m.pct === 'number' && typeof m.minPct === 'number'
    ? `Its margin is ${m.pct} %, under the ${m.minPct} % minimum`
    : 'Its margin is under the company’s minimum';
}

/** "3 lines on 2 estimates wait for your OK", "1 line and 1 option under the minimum margin on …". */
export function waitingSummary(es: readonly WaitingEstimate[]): string {
  const lines = es.reduce((s, e) => s + e.options.reduce((t, o) => t + o.lines.length, 0), 0);
  const margins = es.reduce((s, e) => s + e.options.filter((o) => o.margin).length, 0);
  const parts = [
    ...(lines ? [`${lines} ${lines === 1 ? 'line' : 'lines'}`] : []),
    ...(margins ? [`${margins} ${margins === 1 ? 'option' : 'options'} under the minimum margin`] : []),
  ];
  const one = lines + margins === 1;
  return `${parts.join(' and ') || '0 lines'} on ${es.length} ${es.length === 1 ? 'estimate' : 'estimates'} ${one ? 'waits' : 'wait'} for your OK.`;
}

/** How many estimates the section reads at first, and at most (estimates.awaitingPriceOk's own cap). */
export const PRICE_OK_FIRST = 50;
export const PRICE_OK_MAX = 500;

/**
 * When the list is cut, it says so, with the true count (the server's `total`): "The first 50 of 64
 * are shown." Null when every one is.
 */
export function cutText(shown: number, total: number): string | null {
  return total > shown ? `The first ${shown} of ${total} are shown.` : null;
}

/** "Signed by Mark Henderson on Oct 20, 9:30 a.m.: OK it and the sale goes through." (BL-059) */
export function signedWaitingText(w: { signerName: string; signedAt: string }, timeZone: string): string {
  const when = new Intl.DateTimeFormat('en-CA', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone,
  }).format(new Date(w.signedAt));
  return `Signed by ${w.signerName} on ${when}, waiting for your OK: OK it and the sale goes through.`;
}
